import { tr } from '@util/tr';

export const MISSING_PAYMENT_TYPE = 'missing-payment-method';

export interface MissingCommercialDocumentPaymentFields {
  missingPaymentMode: boolean;
  missingPaymentMethod: boolean;
  missingPaid: boolean;
  unsavedPaymentChanges: boolean;
}

/**
 * Resolves a user-visible message from CRM sync / receipt API responses (including HTTP 400 Problem bodies).
 */
export function resolveSyncActionErrorMessage(
  response: unknown,
  fallbackMessage: string
): string {
  if (!response || typeof response !== 'object') {
    return fallbackMessage;
  }

  const body = response as Record<string, unknown>;
  if (isMissingPaymentSyncError(body)) {
    return tr('receipt.missingPaymentForCommercialDocument');
  }

  const message = typeof body.message === 'string' ? body.message.trim() : '';
  if (message) {
    return message;
  }

  return fallbackMessage;
}

export function isMissingPaymentSyncError(response: unknown): boolean {
  if (!response || typeof response !== 'object') {
    return false;
  }
  const body = response as Record<string, unknown>;
  const type = typeof body.type === 'string' ? body.type.trim() : '';
  if (type === MISSING_PAYMENT_TYPE) {
    return true;
  }
  const errorCode = typeof body.errorCode === 'string' ? body.errorCode.trim() : '';
  return errorCode === 'MISSING_PAYMENT_METHOD';
}

export function getMissingCommercialDocumentPaymentFields(
  paymentMode: { id?: string } | null | undefined,
  paymentMethod: { id?: string } | null | undefined,
  paid: boolean | null | undefined,
  unsavedPaymentChanges = false
): MissingCommercialDocumentPaymentFields {
  return {
    missingPaymentMode: !paymentMode?.id,
    missingPaymentMethod: !paymentMethod?.id,
    missingPaid: !paid,
    unsavedPaymentChanges: !!unsavedPaymentChanges,
  };
}

export function hasCommercialDocumentPaymentBlockers(
  fields: MissingCommercialDocumentPaymentFields
): boolean {
  return (
    fields.missingPaymentMode ||
    fields.missingPaymentMethod ||
    fields.missingPaid ||
    fields.unsavedPaymentChanges
  );
}

/** Body shape compatible with {@link resolveSyncActionErrorMessage} / ExternalSyncButton. */
export function missingPaymentSyncFailureBody(): { success: false; type: string } {
  return { success: false, type: MISSING_PAYMENT_TYPE };
}
